/**
 * ListingsFiltersModal - Filters modal/drawer for listings browse
 *
 * Diamond Architecture (Pure BFF): Uses server action for filters metadata.
 * Browser never talks directly to Go backend.
 *
 * Data fetching: TanStack Query wrapping server action (Option A from architecture).
 * - Automatic race condition handling (stale queries discarded)
 * - keepPreviousData for smooth histogram transitions while refetching
 * - Debounced filter key to avoid hammering API during slider drags
 * - Cache: instant load if modal is reopened with same filters
 *
 * - Price histogram slider on the Rust pricing SSOT non-linear scale
 * - Bedrooms / Bathrooms counter
 * - Live results count from backend
 * - URL sync via searchParams
 *
 * ONE `Dialog` at every viewport — there is no Drawer branch and there never
 * was one in this file. Below `sm` the DIALOG FOOTER is a column whose items
 * stretch, so the primary action is full-width on a phone by construction
 * (`packages/ui/src/dialog-footer.tsx`, and the box is pinned at 393x852 in
 * `web/e2e/public/listings-filters.spec.ts`). This line said "Drawer on
 * mobile" until §2.10 read it: prose describing a branch the module does not
 * have is the same defect as prose describing a retired param.
 *
 * ★ WHY IT LIVES IN `shared/components/listings/filters/` (the `entity` layer)
 * and not beside the browse feature that used to mount it. The trigger has to
 * be reachable from BOTH the mobile top bar (`feature`) and the desktop navbar
 * (`infra`), and `infra → feature` is refused by the element DAG. `entity` is
 * the layer both may import, so promoting the control is the remedy — never a
 * wider DAG (the `MarketingNavbar` precedent, and the listings omnibox after
 * it). `ListingsFiltersControl` beside this file is what mounts it.
 */

import { useRouter, useSearchParams } from '@/shared/i18n/routing';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { bff } from '@/shared/lib/bff';
import { refinementFilterCount } from '@/shared/lib/listings/listings-filter-set';
import { queryKeys } from '@/shared/lib/query-keys';
import { FILTER_ROOMS_MAX, SEARCH_DEBOUNCE_MS } from '@letsgethome/lib/constants';
import { formatPrice } from '@letsgethome/lib/currency';
import { clearsOnClearAll, FilterField } from '@letsgethome/lib/filters';
import { bffError } from '@letsgethome/lib/server-errors';
import {
  applyListingsFilterParams,
  BEDROOMS_STUDIO_TOKEN,
  decodeListingsFilterParams,
  type DecodedListingsFilterParams,
} from '@letsgethome/routes';
import {
  centsBigInt,
  exactInt,
  type ExactInt,
  type LocalizedText,
} from '@letsgethome/types/branded';
import { Button, inFlight } from '@letsgethome/ui/button';
import { Checkbox } from '@letsgethome/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@letsgethome/ui/dialog';
import { useDebouncedValue } from '@letsgethome/ui/hooks/use-debounced-value';
import { Label } from '@letsgethome/ui/label';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Minus, Plus } from 'lucide-react';
import { useCallback, useState } from 'react';

import { PriceHistogramSlider } from './PriceHistogramSlider';
import { useListingsCanvasScope } from './use-listings-browse-scope';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

interface FiltersState {
  bedrooms: number | null;
  bathrooms: number | null;
  minPriceCents: bigint | null;
  maxPriceCents: bigint | null;
  /**
   * U-1: `pets_allowed` reached the wire, the BFF and the badge count but had
   * no control anywhere, so it could never be set. This is that control's
   * state.
   */
  petsAllowed: boolean;
}

/**
 * The modal's REFINEMENT slice of the codec vocabulary, in the modal's own
 * value types. These two functions are the only translation between the two
 * shapes; the param NAMES stay inside `@letsgethome/routes`.
 */
function filtersStateFromParams(params: DecodedListingsFilterParams): FiltersState {
  const int = (raw: string | undefined): number | null => {
    if (raw === undefined) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  };
  const cents = (raw: string | undefined): bigint | null => {
    if (raw === undefined) return null;
    try {
      return BigInt(raw);
    } catch {
      return null;
    }
  };
  return {
    // D11: `studio` is a bedrooms value of its own — 0, a studio exactly.
    bedrooms: params.bedrooms === BEDROOMS_STUDIO_TOKEN ? 0 : int(params.bedrooms),
    bathrooms: int(params.bathrooms),
    minPriceCents: cents(params.minPrice),
    maxPriceCents: cents(params.maxPrice),
    petsAllowed: params.petsAllowed === 'true',
  };
}

function filtersStateToParams(state: FiltersState): DecodedListingsFilterParams {
  return {
    bedrooms: state.bedrooms === 0 ? BEDROOMS_STUDIO_TOKEN : state.bedrooms?.toString(),
    bathrooms: state.bathrooms?.toString(),
    minPrice: state.minPriceCents?.toString(),
    maxPrice: state.maxPriceCents?.toString(),
    // Absent rather than 'false': the wire's false IS "no pets filter", so an
    // off toggle leaves no trace in the URL.
    petsAllowed: state.petsAllowed ? 'true' : undefined,
  };
}

interface ListingsFiltersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// NOTE: the DEFAULT_RENT_RANGE/DEFAULT_SALE_RANGE fallback constants (and the
// server-fetched price-range-config plumbing) are deliberately GONE. The slider
// scale now comes from the compiled Rust pricing SSOT (`pricing_buckets` via
// @letsgethome/lib/wasm) — byte-for-byte the same crate the backend uses to
// build the histogram — so there is no copy left to drift (the fallback had
// already drifted: rent $100k ceiling vs the server's $50k).

// ----------------------------------------------------------------------------
// Counter Component
// ----------------------------------------------------------------------------

function Counter({
  field,
  label,
  value,
  onChange,
  zeroLabel,
  // The bedrooms/bathrooms stepper maximum — the Rust filters SSOT's decision,
  // shared with both mobile steppers (the drifted 5/8/10 caps are gone).
  max = FILTER_ROOMS_MAX,
}: {
  /**
   * Which dimension this stepper is, as a stable DOM hook.
   *
   * The two steppers are otherwise distinguishable only by their LOCALIZED
   * label, so a browser assertion about "the bedrooms stepper" would either be
   * pinned to English copy or have to count rows — both of which go quiet the
   * moment the dialog gains a third counter. Closed union, so a new counter
   * has to name itself. (`data-counter-step` on the steppers rather than a
   * second `data-slot`: `Button` publishes `data-slot="button"` and a caller
   * overriding it would silently remove the primitive's own hook.)
   */
  field: 'bedrooms' | 'bathrooms';
  label: LocalizedText;
  value: number | null;
  onChange: (value: number | null) => void;
  /**
   * D11 — when present, `0` is a real value with this label (bedrooms: a
   * studio): the steps run Any → Studio → 1 → … Without it the smallest value
   * is 1 (bathrooms: never 0 — R-5), so "Any" steps straight to 1.
   */
  zeroLabel?: LocalizedText;
  max?: number;
}) {
  const t = useTranslations('listings.filters');
  const tA11y = useTranslations('common');
  // The smallest real value; below it is "Any".
  const floor = zeroLabel === undefined ? 1 : 0;

  const handleDecrement = () => {
    if (value === null) return;
    onChange(value <= floor ? null : value - 1); // below the floor: "Any"
  };

  const handleIncrement = () => {
    if (value === null) {
      onChange(floor);
    } else if (value < max) {
      onChange(value + 1);
    }
  };

  const shown = value === null ? t('any') : value === 0 && zeroLabel ? zeroLabel : value;

  return (
    <div
      data-slot="filter-counter"
      data-field={field}
      className="flex items-center justify-between py-3"
    >
      <span className="text-sm font-medium text-foreground">{label}</span>
      <div className="flex items-center gap-3">
        <Button
          type="button"
          data-counter-step="decrement"
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded-full"
          aria-label={tA11y('a11y.decrease', { label })}
          onClick={handleDecrement}
          disabled={value === null}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <span className="min-w-8 text-center text-sm font-medium">{shown}</span>
        <Button
          type="button"
          data-counter-step="increment"
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded-full"
          aria-label={tA11y('a11y.increase', { label })}
          onClick={handleIncrement}
          disabled={value !== null && value >= max}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------------

export function ListingsFiltersModal({ open, onOpenChange }: ListingsFiltersModalProps) {
  const t = useTranslations('listings.filters');
  const router = useRouter();
  const searchParams = useSearchParams();
  // THE SCOPE THIS DIALOG IS PREVIEWING (§2.10). It used to arrive as three
  // props — `mode`, `estateId`, `districtId` — from the one caller that
  // happened to mount it, which made "a second mount with a scope that
  // disagrees with the trigger's badge" a representable state rather than an
  // unlikely one. It is the same derivation the trigger reads, so the preview
  // and the badge describe one search by construction.
  const { mode, filters: scopeFilters } = useListingsCanvasScope();
  const estateId = scopeFilters.estateId;
  const districtId = scopeFilters.districtId;

  // Local filter state — initialized from the URL once, THROUGH the shared
  // codec. Reading the param names directly here (`searchParams.get('min_price')`
  // + hand-rolled parseInt/BigInt) is what let this modal drift from the loader
  // that answers it: the names lived in two places, and only one of them was
  // the vocabulary the SSR loader actually decoded.
  const [filters, setFilters] = useState<FiltersState>(() =>
    filtersStateFromParams(decodeListingsFilterParams(searchParams))
  );

  // Debounce filters to avoid hammering API during slider drags — the ONE
  // search-debounce constant (Rust search SSOT), shared with mobile.
  const debouncedFilters = useDebouncedValue(filters, SEARCH_DEBOUNCE_MS);

  // ONE spelling of the price edges, for BOTH the cache key and the request
  // body (R4-2). They used to be four separate `?.toString()` calls that
  // happened to agree; `exactInt` is the MINT the wire type names, so the key
  // and the body cannot drift — and neither can carry a `bigint` into a
  // `JSON.stringify`. That matters twice over here: TanStack's `hashKey` is
  // `JSON.stringify` with a key-sorting replacer, so a bigint in a query key
  // is the same crash one layer up from the wire.
  const minPriceCents: ExactInt | undefined =
    debouncedFilters.minPriceCents === null ? undefined : exactInt(debouncedFilters.minPriceCents);
  const maxPriceCents: ExactInt | undefined =
    debouncedFilters.maxPriceCents === null ? undefined : exactInt(debouncedFilters.maxPriceCents);

  // ---------------------------------------------------------------------------
  // TanStack Query: Fetch filters metadata (histogram + results count)
  // - queryKey: null when modal closed → query is disabled (no fetch)
  // - keepPreviousData: shows old histogram while new one loads (smooth UX)
  // - Server action as queryFn: maintains Pure BFF (browser → Next.js → Go)
  // ---------------------------------------------------------------------------
  const { data: metadata, isLoading } = useQuery({
    queryKey: open
      ? queryKeys.filters.metadata(
          mode,
          estateId,
          districtId,
          minPriceCents ?? null,
          maxPriceCents ?? null,
          debouncedFilters.bedrooms,
          debouncedFilters.bathrooms,
          debouncedFilters.petsAllowed
        )
      : [],
    queryFn: async () => {
      const result = await bff('/api/search/filters-metadata', {
        mode,
        estateId,
        districtId,
        minPriceCents,
        maxPriceCents,
        minBedrooms: debouncedFilters.bedrooms ?? undefined,
        minBathrooms: debouncedFilters.bathrooms ?? undefined,
        // U-4: without these the count previewed a DIFFERENT filter set than
        // Apply was about to produce.
        petsAllowed: debouncedFilters.petsAllowed || undefined,
      });

      if (!result.success) {
        throw bffError(result.error, 'Failed to load filters');
      }

      const data = result.data!;
      return {
        histogram: data.histogram,
        resultsCount: data.resultsCount,
      };
    },
    enabled: open,
    placeholderData: keepPreviousData,
    staleTime: 30_000, // 30s — histogram doesn't change frequently
    retry: 1,
  });

  // Handle price change from slider
  const handlePriceChange = useCallback((min: bigint | null, max: bigint | null) => {
    setFilters((prev) => ({
      ...prev,
      minPriceCents: min,
      maxPriceCents: max,
    }));
  }, []);

  // Apply filters to the URL through the codec's write door: it sets what is
  // present, removes what is not, and leaves every non-filter param alone.
  // A page reset is deliberate — a new filter set is a new result set.
  const handleApply = useCallback(() => {
    const params = applyListingsFilterParams(searchParams, {
      ...filtersStateToParams(filters),
      page: undefined,
    });
    // Same-page query update, not a route navigation.
    void router.setQuery(params);
    onOpenChange(false);
  }, [filters, searchParams, router, onOpenChange]);

  // Reset all filters. WHICH fields a "clear all" wipes is the Rust filters
  // SSOT's decision (domain_core::filters, shared with both mobile apps):
  // refinements (prices, rooms, pets) reset; the browsing context (mode,
  // district/estate, query, map bounds — not state here) stays sticky. Only
  // the state mechanics are local; the partition cannot drift from Rust.
  const handleReset = useCallback(() => {
    setFilters((prev) => ({
      bedrooms: clearsOnClearAll(FilterField.Bedrooms) ? null : prev.bedrooms,
      bathrooms: clearsOnClearAll(FilterField.Bathrooms) ? null : prev.bathrooms,
      minPriceCents: clearsOnClearAll(FilterField.MinPrice) ? null : prev.minPriceCents,
      maxPriceCents: clearsOnClearAll(FilterField.MaxPrice) ? null : prev.maxPriceCents,
      petsAllowed: clearsOnClearAll(FilterField.PetsAllowed) ? false : prev.petsAllowed,
    }));
  }, []);

  // Active REFINEMENTS, counted by the Rust filters SSOT — the same function
  // the browse badge uses, so "a price range counts once" cannot mean one
  // thing here and another there.
  const activeCount = refinementFilterCount(filters);

  // Use metadata or defaults
  const histogram = metadata?.histogram ?? [];
  const resultsCount = metadata?.resultsCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>

        <div className="divide-y divide-border">
          {/* Price Range with Histogram */}
          <div className="py-4">
            <span className="text-sm font-medium text-foreground mb-4 block">
              {t('priceRange')}
            </span>
            <PriceHistogramSlider
              buckets={histogram}
              isRent={mode === 'rent'}
              minPrice={filters.minPriceCents}
              maxPrice={filters.maxPriceCents}
              onChange={handlePriceChange}
              formatPrice={(cents) => formatPrice(centsBigInt(cents))}
            />
          </div>

          {/* Rooms Section */}
          <div className="py-2">
            <span className="text-sm font-medium text-foreground mb-2 block pt-2">
              {t('rooms')}
            </span>
            {/* Bedrooms */}
            <Counter
              field="bedrooms"
              label={t('bedrooms')}
              zeroLabel={t('studio')}
              value={filters.bedrooms}
              onChange={(v) => setFilters((prev) => ({ ...prev, bedrooms: v }))}
            />

            {/* Bathrooms */}
            <Counter
              field="bathrooms"
              label={t('bathrooms')}
              value={filters.bathrooms}
              onChange={(v) => setFilters((prev) => ({ ...prev, bathrooms: v }))}
            />
          </div>

          {/* Pets — the control this filter never had (U-1). */}
          <div className="flex items-center justify-between py-4">
            <Label htmlFor="filter-pets-allowed" className="text-sm font-medium text-foreground">
              {t('petsAllowed')}
            </Label>
            <Checkbox
              id="filter-pets-allowed"
              checked={filters.petsAllowed}
              onCheckedChange={(checked) =>
                setFilters((prev) => ({ ...prev, petsAllowed: checked === true }))
              }
            />
          </div>
        </div>

        {/* `justify`, not a `className`: the raw override this carried
            (`flex-row gap-2 sm:justify-between`) beat the primitive's
            `flex-col-reverse` at EVERY width through tailwind-merge while its
            `sm:justify-between` applied only from `sm` up, so below `sm` the
            two controls sat left-packed in a row with 84 px of dead footer to
            the right of the primary action (obs. 2c). See
            `@letsgethome/ui/dialog-footer`. */}
        <DialogFooter justify="between">
          <Button type="button" variant="ghost" onClick={handleReset} disabled={activeCount === 0}>
            {t('reset')}
          </Button>
          <Button type="button" onClick={handleApply} loading={inFlight(isLoading)}>
            {resultsCount !== undefined ? t('showResults', { count: resultsCount }) : t('apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
